import React from "react";
import { Link } from "react-router-dom";
import { SearchIcon, XIcon } from "lucide-react";
import { searchSuggestions } from "../../data/intelligence";
import { IntelligenceSearch as SearchState } from "../../hooks/useIntelligenceSearch";
import { formatUpdated } from "../../utils/intelligence";
import { EntryPreview } from "./EntryPreview";
type IntelligenceSearchProps = {
  search: SearchState;
};
export function IntelligenceSearch({
  search
}: IntelligenceSearchProps) {
  const {
    query,
    setQuery,
    results,
    selected,
    selectEntry,
    findEntry,
    isSearching
  } = search;
  return <div id="intel-search" className="scroll-mt-28" data-id="element-332" data-mp="3dd8s2wd6abb8">
      <form role="search" onSubmit={e => e.preventDefault()} className="mt-14" data-id="element-333" data-mp="3dcgzluevgtp6">
        <label htmlFor="intel-query" className="sr-only" data-id="element-334" data-mp="3iaqrnw5ave6k">
          Search Constant Intelligence
        </label>
        <div className="flex items-center gap-4 border-b-2 border-navy pb-4 focus-within:border-oxblood" data-id="element-335" data-mp="21w8sdfohwu76">
          <SearchIcon aria-hidden strokeWidth={1.25} className="h-7 w-7 shrink-0 text-navy" data-id="element-336" data-mp="26y7wirs27v56" />
          <input id="intel-query" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search a state, a country, a system, or an issue" autoComplete="off" className="min-w-0 flex-1 bg-transparent font-display text-[clamp(1.5rem,3.2vw,2.5rem)] text-navy placeholder:text-charcoal/35 focus:outline-none [&::-webkit-search-cancel-button]:hidden" data-id="element-337" data-mp="1efh30eococ6a" />
          {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="shrink-0 p-1 text-charcoal/60 transition-colors duration-150 hover:text-oxblood" data-id="element-338" data-mp="1se72g0yn6nxt">
              <XIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-339" data-mp="2g41c69nnzwdu" />
            </button>}
        </div>
      </form>

      <div className="mt-5 flex flex-wrap items-baseline gap-x-6 gap-y-2" data-id="element-340" data-mp="2c3ingoh2j6fv">
        <span className="eyebrow text-charcoal/60" data-id="element-341" data-mp="2j65xoo8dqcne">Try</span>
        {searchSuggestions.map(s => <button key={s} type="button" onClick={() => setQuery(s)} className={`whitespace-nowrap border-b py-1 text-[13px] font-medium transition-colors duration-150 ${query === s ? "border-oxblood text-oxblood" : "border-stone text-charcoal/75 hover:border-oxblood hover:text-oxblood"}`} data-id="element-342" data-mp="2o4qv6fw8yi85">
            {s}
          </button>)}
      </div>

      <div className="mt-14 grid grid-cols-12 gap-x-6 gap-y-10" data-id="element-343" data-mp="36u6yfazbgos9">
        <div className="col-span-12 lg:col-span-5" data-id="element-344" data-mp="3bu1r9k2njofy">
          <p className="eyebrow text-charcoal/60" aria-live="polite" data-id="element-345" data-mp="3bvcrj9ww2e2b">
            {isSearching ? `${results.length} ${results.length === 1 ? "result" : "results"}` : "Recently updated"}
          </p>

          {results.length === 0 ? <div className="mt-4 border-t border-navy py-10" data-id="element-346" data-mp="1mfdc5b2ofse8">
              <p className="font-display text-2xl text-navy" data-id="element-347" data-mp="1mhr7ll19r6mb">Nothing matches “{query}” yet.</p>
              <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-charcoal/70" data-id="element-348" data-mp="3ltrpelm7rnw">
                Try a state, a country, a system, or a topic — or ask us directly.
              </p>
              <Link to="/contact" className="link-rule mt-6" data-id="element-349" data-mp="3qy4fo9l0lxm8">
                Ask Constant
              </Link>
            </div> : <ul className="mt-4 border-t border-navy" data-id="element-350" data-mp="mj4qky3c4j2t">
              {results.map(r => {
            const active = selected?.id === r.id;
            return <li key={r.id} data-id="element-351" data-mp="ifoy8dyat4vx">
                    <button type="button" onClick={() => selectEntry(r.id)} aria-current={active ? "true" : undefined} className={`block w-full border-b border-stone py-4 pr-4 text-left transition-colors duration-150 ${active ? "border-l-2 border-l-oxblood bg-stone/30 pl-4" : "pl-0 hover:bg-stone/20"}`} data-id="element-352" data-mp="di6tkzrbdbtq">
                      <span className="flex items-baseline justify-between gap-4" data-id="element-353" data-mp="kzbhlp95ycof">
                        <span className="eyebrow text-[10px] text-oxblood" data-id="element-354" data-mp="pt604pd18llx">{r.type}</span>
                        <span className="text-[11px] tabular-nums text-charcoal/55" data-id="element-355" data-mp="c21746466rkk">{formatUpdated(r.updated, "MMM d")}</span>
                      </span>
                      <span className={`mt-2 block font-display text-xl leading-snug ${active ? "text-oxblood" : "text-navy"}`} data-id="element-356" data-mp="14olxb38wsxmj">
                        {r.title}
                      </span>
                    </button>
                  </li>;
          })}
            </ul>}
        </div>

        <div className="col-span-12 lg:col-span-7" data-id="element-357" data-mp="zp984bukfegs">
          <div className="lg:sticky lg:top-28" data-id="element-358" data-mp="zrvh8fx3u214">
            {selected && <EntryPreview key={selected.id} entry={selected} findEntry={findEntry} onSelect={selectEntry} data-id="element-359" data-mp="3fgf2kswbpwj8" />}
          </div>
        </div>
      </div>
    </div>;
}